import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { Typography, Box, Snackbar, Alert, TableSortLabel } from '@mui/material';
import { useSystemParameters } from './SystemParameters.hooks';
import { SystemParametersProps } from '../../../types/system-parameters.types';
import { DeleteCategoryConfirmationModal } from '../../modals/confirmation';
import CommonPagination from '../../common/Pagination';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { usePermission } from '../../../hooks/usePermission';
import { SETTINGS_PERMISSIONS } from '../../../constants/permissions.constants';
import { FlagSwitch, PhoneNumberInput } from '../../../components/ui';
import { compareValuesNaturally } from '../../../utils/sort/compareValues';
import {
  SystemParametersContainer,
  TabsContainer,
  Tab,
  TabIcon,
  TabLabelContainer,
  TabLabel,
  TabCount,
  MainContent,
  ContentHeader,
  ContentActions,
  AddButton,
  SearchContainer,
  SearchField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableDataCell,
  ActionButtons,
  EditButton,
  DeleteButton,
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalContent,
  FormField,
  FormLabel,
  FormInput,
  ColorInput,
  TextAreaInput,
  ModalActions,
  CancelButton,
  SaveButton,
  CardsGridContainer,
  ItemCard,
  CardHeader,
  CardTitle,
  CardActions,
  CardActionButton,
  CardContent,
  CardField,
  CardFieldLabel,
  CardFieldValue,
  ColorBadge,
  ColorDot,
  CardsEmptyState,
} from './SystemParameters.styles';
import EmptyStateMessage from '../../common/EmptyStateMessage';

const PARAMETER_FIELD_LABELS: Record<string, string> = {
  nombre: 'Nombre',
  descripcion: 'Descripción',
  telefono: 'Teléfono',
  email: 'Correo',
  contacto: 'Persona de contacto',
  color: 'Color',
  codigo: 'Código',
  icono: 'Icono',
  activo: 'Activo',
};

function formatParameterFieldLabel(field: string): string {
  const mapped = PARAMETER_FIELD_LABELS[field];
  if (mapped) return mapped;
  return field
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SystemParameters: React.FC<SystemParametersProps> = ({ onParameterUpdate }) => {
  const canAdd = usePermission(SETTINGS_PERMISSIONS.PROPERTIES.ADD);
  const canEdit = usePermission(SETTINGS_PERMISSIONS.PROPERTIES.EDIT);
  const canDelete = usePermission(SETTINGS_PERMISSIONS.PROPERTIES.DELETE);

  const {
    selectedCategory,
    searchTerm,
    showAddModal,
    editingItem,
    loading,
    error,
    showDeleteModal,
    itemToDelete,
    notification,
    categories,
    currentCategory,
    filteredData,
    pagination,
    setSelectedCategory,
    setShowAddModal,
    setShowDeleteModal,
    handleAdd,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleSave,
    handleSearchChange,
    handlePageChange,
    handleItemsPerPageChange,
    handleCloseNotification,
  } = useSystemParameters();

  const [tableSort, setTableSort] = useState<{
    sortBy: string | null;
    sortOrder: 'asc' | 'desc';
  }>({ sortBy: null, sortOrder: 'asc' });

  useEffect(() => {
    setTableSort({ sortBy: null, sortOrder: 'asc' });
  }, [selectedCategory]);

  const handleTableSort = useCallback((field: string) => {
    setTableSort((prev) => {
      if (prev.sortBy === field) {
        return {
          sortBy: field,
          sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }
      return { sortBy: field, sortOrder: 'asc' };
    });
  }, []);

  const sortedTableData = useMemo(() => {
    if (tableSort.sortBy == null) {
      return filteredData;
    }
    const { sortBy, sortOrder } = tableSort;
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      const comparison = compareValuesNaturally(aValue, bValue);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, tableSort]);

  // Cerrar con ESC
  useEscapeKey(() => {
    if (showAddModal && !loading) {
      setShowAddModal(false);
    }
  }, showAddModal && !loading);

  // Form state for modal
  const [formData, setFormData] = useState<Record<string, string | boolean>>({
    nombre: '',
    descripcion: '',
    contacto: '',
    telefono: '',
    email: '',
    color: '',
    codigo: '',
    icono: '',
    activo: true,
  });

  // Update form data when editing item changes
  useEffect(() => {
    if (editingItem) {
      // Initialize formData with all possible fields from editingItem
      const newFormData: Record<string, string | boolean> = {};
      currentCategory.fields.forEach((field) => {
        if (field === 'activo') {
          newFormData[field] = (editingItem as any)[field] ?? true;
        } else {
          newFormData[field] = (editingItem as any)[field] || '';
        }
      });
      // Also include activo if it exists in editingItem but not in fields
      if ((editingItem as any).activo !== undefined && !currentCategory.fields.includes('activo')) {
        newFormData.activo = (editingItem as any).activo;
      }
      setFormData(newFormData);
    } else {
      // Reset formData based on current category fields
      const newFormData: Record<string, string | boolean> = {};
      currentCategory.fields.forEach((field) => {
        // Si es el campo color, usar el valor por defecto
        if (field === 'color') {
          newFormData[field] = '#9CA3AF';
        } else if (field === 'activo') {
          newFormData[field] = true;
        } else {
          newFormData[field] = '';
        }
      });
      setFormData(newFormData);
    }
  }, [editingItem, currentCategory.fields]);

  // Resetear formulario cuando se abre el modal para agregar (no editar)
  useEffect(() => {
    if (showAddModal && !editingItem) {
      const newFormData: Record<string, string | boolean> = {};
      currentCategory.fields.forEach((field) => {
        // Si es el campo color, usar el valor por defecto
        if (field === 'color') {
          newFormData[field] = '#9CA3AF';
        } else if (field === 'activo') {
          newFormData[field] = true;
        } else {
          newFormData[field] = '';
        }
      });
      setFormData(newFormData);
    }
  }, [showAddModal, editingItem, currentCategory.fields]);

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Validar si el formulario es válido (nombre es requerido)
  const isFormValid = useMemo(() => {
    const nombre = (typeof formData.nombre === 'string' ? formData.nombre.trim() : '') || '';
    return nombre.length > 0;
  }, [formData.nombre]);

  const handleModalSave = () => {
    handleSave(formData);
  };

  // Colores únicos para cada categoría
  const categoryColors = {
    tipoProducto: '#3b82f6', // Azul
    etiquetas: '#f59e0b', // Naranja
    material: '#10b981', // Verde
    proveedor: '#8b5cf6', // Púrpura
    metodosPago: '#06b6d4', // Cyan
  };

  return (
    <>
      <SystemParametersContainer>
        {/* Main Content */}
        <MainContent>
          {/* Categories Cards Navigation */}
          <TabsContainer>
            {Object.entries(categories).map(([key, category]) => {
              const Icon = category.icon;
              const isActive = selectedCategory === key;
              const categoryColor = categoryColors[key as keyof typeof categoryColors];
              return (
                <Tab
                  key={key}
                  active={isActive}
                  categoryColor={categoryColor}
                  onClick={() => setSelectedCategory(key)}
                  tabIndex={0}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedCategory(key);
                    }
                  }}
                >
                  <TabIcon active={isActive} categoryColor={categoryColor}>
                    <Icon />
                  </TabIcon>
                  <TabLabelContainer>
                    <TabLabel active={isActive} categoryColor={categoryColor}>
                      {category.title}
                    </TabLabel>
                    <TabCount active={isActive} categoryColor={categoryColor}>
                      {category.totalCount !== undefined
                        ? category.totalCount
                        : category.data.length}
                    </TabCount>
                  </TabLabelContainer>
                </Tab>
              );
            })}
          </TabsContainer>
          {/* Content Header */}
          <ContentHeader>
            <ContentActions>
              {canAdd && (
                <AddButton onClick={handleAdd}>
                  <Add sx={{ fontSize: 16, marginRight: 0.5 }} />
                  Agregar
                </AddButton>
              )}
            </ContentActions>
          </ContentHeader>

          {/* Search Section */}
          <SearchContainer>
            <SearchField
              fullWidth
              label="Búsqueda General"
              placeholder={`Buscar en ${currentCategory.title.toLowerCase()}...`}
              value={searchTerm}
              onChange={handleSearchChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <Search sx={{ fontSize: 16, marginRight: 1, color: 'text.secondary' }} />
                ),
              }}
            />
          </SearchContainer>

          {/* Cards View para categorías simples (Tipo de Producto, Etiquetas, Material) */}
          {selectedCategory !== 'proveedor' ? (
            <>
              {loading ? (
                <Box sx={{ textAlign: 'center', padding: '40px' }}>
                  <Typography color="text.secondary">Cargando...</Typography>
                </Box>
              ) : filteredData.length === 0 ? (
                <CardsEmptyState>
                  <EmptyStateMessage message="No se encontraron elementos" minHeight="auto" />
                </CardsEmptyState>
              ) : (
                <CardsGridContainer>
                  {filteredData.map((item) => (
                    <ItemCard key={item.id}>
                      <CardHeader>
                        <CardTitle>
                          {selectedCategory === 'etiquetas' && item.color ? (
                            <ColorBadge color={item.color}>
                              <ColorDot color={item.color} />
                              <span>{item.nombre}</span>
                            </ColorBadge>
                          ) : (
                            item.nombre
                          )}
                        </CardTitle>
                        <CardActions>
                          {canEdit && (
                            <CardActionButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(item);
                              }}
                              title="Editar"
                              sx={{ color: 'primary.light' }}
                            >
                              <Edit sx={{ fontSize: 18 }} />
                            </CardActionButton>
                          )}
                          {canDelete && (
                            <CardActionButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item);
                              }}
                              title="Eliminar"
                              sx={{ color: 'primary.light' }}
                            >
                              <Delete sx={{ fontSize: 18 }} />
                            </CardActionButton>
                          )}
                        </CardActions>
                      </CardHeader>
                      <CardContent>
                        {/* Mostrar descripción sin label si existe */}
                        {item.descripcion && (
                          <CardFieldValue sx={{ marginTop: 0 }}>{item.descripcion}</CardFieldValue>
                        )}
                        {/* Otros campos con sus labels */}
                        {currentCategory.fields
                          .filter(
                            (field) =>
                              field !== 'nombre' &&
                              field !== 'descripcion' &&
                              !(selectedCategory === 'etiquetas' && field === 'color'),
                          )
                          .map((field) => (
                            <CardField key={field}>
                              <CardFieldLabel>{formatParameterFieldLabel(field)}</CardFieldLabel>
                              <CardFieldValue>
                                {field === 'activo'
                                  ? item[field]
                                    ? 'Activo'
                                    : 'Inactivo'
                                  : item[field] || (
                                      <Box
                                        component="span"
                                        sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                                      >
                                        Sin {field}
                                      </Box>
                                    )}
                              </CardFieldValue>
                            </CardField>
                          ))}
                      </CardContent>
                    </ItemCard>
                  ))}
                </CardsGridContainer>
              )}
            </>
          ) : (
            /* Table View para Proveedores */
            <Box
              sx={(theme) => ({
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                minHeight: 0,
                px: 0,
                ...(theme.palette.mode === 'dark' ? { boxShadow: 'none' } : {}),
              })}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {currentCategory.fields.map((field) => (
                        <TableHeaderCell key={field}>
                          <TableSortLabel
                            active={tableSort.sortBy === field}
                            direction={tableSort.sortBy === field ? tableSort.sortOrder : 'asc'}
                            onClick={() => handleTableSort(field)}
                          >
                            {formatParameterFieldLabel(field)}
                          </TableSortLabel>
                        </TableHeaderCell>
                      ))}
                      <TableHeaderCell sx={{ textAlign: 'right' }}>Acciones</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableDataCell
                          colSpan={currentCategory.fields.length + 1}
                          sx={{ textAlign: 'center', padding: '40px' }}
                        >
                          Cargando...
                        </TableDataCell>
                      </TableRow>
                    ) : (
                      sortedTableData.map((item) => (
                        <TableRow key={item.id} hover>
                          {currentCategory.fields.map((field) => (
                            <TableDataCell key={field}>
                              {field === 'email' ? (
                                <Box component="span" sx={{ color: 'primary.light' }}>
                                  {item[field] || '-'}
                                </Box>
                              ) : (
                                item[field] || '-'
                              )}
                            </TableDataCell>
                          ))}
                          <TableDataCell sx={{ textAlign: 'right' }}>
                            <ActionButtons>
                              {canEdit && (
                                <EditButton onClick={() => handleEdit(item)} title="Editar">
                                  <Edit sx={{ fontSize: 16 }} />
                                </EditButton>
                              )}
                              {canDelete && (
                                <DeleteButton onClick={() => handleDelete(item)} title="Eliminar">
                                  <Delete sx={{ fontSize: 16 }} />
                                </DeleteButton>
                              )}
                            </ActionButtons>
                          </TableDataCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Paginación uniforme */}
          {pagination.totalItems > 0 && (
            <CommonPagination
              currentPage={pagination.page}
              itemsPerPage={pagination.limit}
              totalItems={pagination.totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={(event) => {
                const newLimit = parseInt(event.target.value as string);
                handleItemsPerPageChange(newLimit);
              }}
            />
          )}

          {/* Empty State - Solo para tabla de proveedores (cards ya tienen su propio empty state) */}
          {selectedCategory === 'proveedor' && filteredData.length === 0 && !loading && (
            <EmptyStateMessage message="No se encontraron elementos" minHeight={200} />
          )}
        </MainContent>

        {/* Modal */}
        {showAddModal && (
          <ModalOverlay open={showAddModal} onClose={() => setShowAddModal(false)}>
            <ModalContainer>
              <ModalHeader>
                <ModalTitle>
                  {editingItem ? 'Editar' : 'Agregar'} {currentCategory.title}
                </ModalTitle>
                <ModalCloseButton onClick={() => setShowAddModal(false)}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </ModalCloseButton>
              </ModalHeader>

              <ModalContent>
                {error && (
                  <Box
                    sx={(theme) => ({
                      padding: '12px',
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.error.dark : '#fee2e2',
                      color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#dc2626',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      border: `1px solid ${theme.palette.error.main}`,
                      fontSize: '14px',
                    })}
                  >
                    {error}
                  </Box>
                )}
                {currentCategory.fields.map((field) => {
                  // Campos obligatorios que deben tener asterisco
                  const isRequired = field === 'nombre';
                  return (
                    <FormField key={field}>
                      <FormLabel>
                        {formatParameterFieldLabel(field)}
                        {isRequired ? ' *' : ''}
                      </FormLabel>
                      {field === 'color' ? (
                        <ColorInput
                          type="color"
                          value={
                            (typeof formData[field as keyof typeof formData] === 'string'
                              ? formData[field as keyof typeof formData]
                              : '#9CA3AF') as string
                          }
                          onChange={(e) => handleFormChange(field, e.target.value)}
                        />
                      ) : field === 'activo' ? (
                        <FlagSwitch
                          checked={
                            formData[field as keyof typeof formData] === true ||
                            formData[field as keyof typeof formData] === 'true'
                          }
                          onChange={(e, checked) => handleFormChange(field, checked)}
                          showLabel
                          label={
                            formData[field as keyof typeof formData] === true ||
                            formData[field as keyof typeof formData] === 'true'
                              ? 'Activo'
                              : 'Inactivo'
                          }
                        />
                      ) : field === 'descripcion' ? (
                        <TextAreaInput
                          multiline
                          rows={3}
                          value={formData[field as keyof typeof formData] || ''}
                          onChange={(e) => handleFormChange(field, e.target.value)}
                        />
                      ) : field === 'telefono' ? (
                        <PhoneNumberInput
                          value={(formData[field as keyof typeof formData] || '') as string}
                          onChange={(value) => handleFormChange(field, value)}
                          nationalNumberLabel="Número"
                          nationalNumberPlaceholder="Ingrese número"
                        />
                      ) : (
                        <FormInput
                          type={field === 'email' ? 'email' : 'text'}
                          value={formData[field as keyof typeof formData] || ''}
                          onChange={(e) => handleFormChange(field, e.target.value)}
                        />
                      )}
                    </FormField>
                  );
                })}

                <ModalActions>
                  <CancelButton onClick={() => setShowAddModal(false)}>Cancelar</CancelButton>
                  <SaveButton onClick={handleModalSave} disabled={loading || !isFormValid}>
                    {loading ? 'Guardando...' : editingItem ? 'Actualizar' : 'Guardar'}
                  </SaveButton>
                </ModalActions>
              </ModalContent>
            </ModalContainer>
          </ModalOverlay>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteCategoryConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          itemName={itemToDelete?.nombre || ''}
          itemCode={undefined}
          isLoading={loading}
        />
      </SystemParametersContainer>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 1400 }} // Asegurar que esté por encima de modales
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SystemParameters;
